import {
  AcademicYearDto,
  IModuleSelectionClient,
  LocationDto,
  ModuleCategoryDto,
  ModuleDto,
  ModuleExecutionDto,
  SemesterInfoDto,
} from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createAppThunk } from '../../store/app-thunk';
import { semesterSelectionActions } from './semester-selection.slice';

const fetchModuleExecutionsForYearAsync = createAppThunk(
  'moduleSelection/FETCH_MODULE_EXECUTIONS',
  async (academicYearId: number | undefined, deps: { moduleSelectionClient: IModuleSelectionClient }) => {
    const executions = await deps.moduleSelectionClient.getModuleExecutions(academicYearId);
    return {
      academicYearId,
      executions,
    };
  },
  (r) => ({ moduleSelectionClient: r.resolve('IModuleSelectionClient') }),
);

const fetchModuleFilter = createAppThunk(
  'moduleSelection/FETCH_MODULE_FILTER',
  async (_, deps: { moduleSelectionClient: IModuleSelectionClient }) => {
    return await deps.moduleSelectionClient.getModuleStructure();
  },
  (r) => ({ moduleSelectionClient: r.resolve('IModuleSelectionClient') }),
);

const getExecutionsAsync = createAppThunk(
  'moduleSelection/GET_EXECUTIONS',
  async (
    {
      academicYearId,
      moduleId,
      categoryId,
      semesterId,
    }: { academicYearId: number; moduleId?: number; categoryId?: number; semesterId?: number },
    deps,
    dispatch,
    getState,
  ) => {
    const state = getState().moduleSelection;
    if (!academicYearId && semesterId) {
      const semester = getState().semesterSelection.semesterFilter.semesters?.find((s) => s.id === semesterId);
      academicYearId = semester?.academicYearId;
    }
    const forYear = state.executions[academicYearId]
      ? state.executions[academicYearId]
      : (await dispatch(fetchModuleExecutionsForYearAsync(academicYearId)).unwrap()).executions;
    return forYear.filter(
      (e) =>
        (!moduleId || e.moduleId === moduleId) &&
        (!categoryId || e.categoryId === categoryId) &&
        (!semesterId || e.semesterId === semesterId),
    );
  },
);

const getModuleExecutionAsync = createAppThunk(
  'moduleSelection/LOAD_MODULE_EXECUTION',
  async (moduleExecutionId: number, deps: { moduleSelectionClient: IModuleSelectionClient }, dispatch, getState) => {
    if (getState().moduleSelection.selectedExecution?.id === moduleExecutionId) {
      return getState().moduleSelection.selectedExecution;
    }

    if (getState().moduleSelection.executions) {
      const loadedExecutions = Object.values(getState().moduleSelection.executions).flat() as ModuleExecutionDto[];
      const execution = loadedExecutions.find((e) => e.id === moduleExecutionId);
      if (execution) {
        return execution;
      }
    }
    const execution = await deps.moduleSelectionClient.getModuleExecution(moduleExecutionId);
    return execution;
  },
  (r) => ({ moduleSelectionClient: r.resolve('IModuleSelectionClient') }),
);

const setFilterAsync = createAppThunk(
  'moduleSelection/SET_FILTER',
  async (
    args: {
      category?: ModuleCategoryDto;
      module?: ModuleDto;
      academicYear?: AcademicYearDto;
      semester?: SemesterInfoDto;
      moduleExecutionId?: number;
    },
    deps,
    dispatch,
    getState,
  ): Promise<{
    module?: ModuleDto;
    academicYear?: AcademicYearDto;
      category?: ModuleCategoryDto;
      execution?: ModuleExecutionDto;
      executions?: ModuleExecutionDto[];
      semester?: SemesterInfoDto;
  }> => {
    if ((getState().moduleSelection.moduleCategories?.length ?? 0) < 1) {
      await dispatch(fetchModuleFilter());
    }

    const execution = args.moduleExecutionId
      ? await dispatch(getModuleExecutionAsync(args.moduleExecutionId)).unwrap()
      : undefined;
    if (execution) {
      const semester = getState().semesterSelection.semesterFilter.semesters?.find(
        (s) => s.id === execution.semesterId,
      );
      const academicYear = getState().semesterSelection.semesterFilter.academicYears?.find(
        (s) => s.id === semester.academicYearId,
      );
      if (academicYear?.id !== getState().semesterSelection.selectedAcademicYear?.id) {
        dispatch(semesterSelectionActions.setSemesterFilter({ academicYear, semester }));
      }
      dispatch(moduleSelectionActions.setModuleExecution(execution));
      if (!getState().moduleSelection.executions[academicYear.id]) {
        await dispatch(fetchModuleExecutionsForYearAsync(academicYear.id)).unwrap();
      }
      const module = getState().moduleSelection.selectedModule;
      const category = getState().moduleSelection.selectedCategory;
      return {
        academicYear,
        semester,
        module,
        category,
        execution,
      };
    }

    let category = args.category ?? getState().moduleSelection.selectedCategory;
    if (args.module && args.module?.categoryId !== category?.id) {
      category = getState().moduleSelection.moduleCategories.find((c) => args.module.categoryId === c.id);
    }

    const academicYear = args.academicYear ?? getState().semesterSelection.selectedAcademicYear;
    const semester = args.semester ?? getState().semesterSelection.selectedSemester;
    if (
      (args.academicYear && args.academicYear?.id !== getState().semesterSelection.selectedAcademicYear?.id) ||
      (args.semester && args.semester?.id !== getState().semesterSelection.selectedSemester?.id)
    ) {
      dispatch(semesterSelectionActions.setSemesterFilter({ academicYear, semester }));
    }

    if (!getState().moduleSelection.executions[academicYear.id]) {
      await dispatch(fetchModuleExecutionsForYearAsync(academicYear.id));
    }
    return {
      academicYear,
      module: args.module,
      category,
    };
  },
);

const initialState = {
  loading: false,
  isLoaded: false,
  errorMessage: null as string,
  moduleCategories: null as ModuleCategoryDto[],
  modules: null as ModuleDto[],
  executions: {} as { [semesterId: number]: ModuleExecutionDto[] },
  selectedCategory: null as ModuleCategoryDto,
  selectedModule: null as ModuleDto,
  selectedExecution: null as ModuleExecutionDto,
  locations: [] as LocationDto[],
};

export type ModuleSelectionState = Readonly<typeof initialState>;

// Reducer

const slice = createSlice({
  name: 'moduleSelection',
  initialState,
  reducers: {
    resetExecutions(state) {
      state.executions = {};
    },
    selectModuleCategory(state, action: PayloadAction<ModuleCategoryDto | undefined>) {
      state.selectedCategory = action.payload;
      state.selectedModule = undefined;
      state.selectedExecution = undefined;
    },
    setModule(state, action: PayloadAction<ModuleDto | undefined>) {
      state.selectedExecution = undefined;
      state.selectedModule = action.payload;
      if (action.payload) {
        state.selectedCategory = state.moduleCategories.find((c) => c.id === action.payload.categoryId);
      }
    },
    setModuleExecution(state, action: PayloadAction<ModuleExecutionDto | undefined>) {
      state.selectedExecution = action.payload;
      if (action.payload) {
        state.selectedModule = state.modules.find((m) => m.id === action.payload.moduleId);
        state.selectedCategory = state.moduleCategories.find((c) => c.id === action.payload.categoryId);
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchModuleExecutionsForYearAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchModuleExecutionsForYearAsync.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload as string;
      })
      .addCase(fetchModuleExecutionsForYearAsync.fulfilled, (state, action) => {
        state.executions[action.payload.academicYearId] = action.payload.executions;
        state.loading = false;
      })
      .addCase(fetchModuleFilter.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchModuleFilter.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload as string;
      })
      .addCase(fetchModuleFilter.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoaded = true;
        state.moduleCategories = action.payload.categories;
        state.modules = action.payload.modules;
        state.selectedCategory =
          action.payload.categories.find((c) => c.id === state.selectedCategory?.id) ?? action.payload.categories[0];
        state.selectedModule = action.payload.modules.find((c) => c.id === state.selectedModule?.id);
        state.locations = action.payload.locations;
      })
      .addCase(setFilterAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(setFilterAsync.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload as string;
      })
      .addCase(setFilterAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCategory = action.payload.category;
        state.selectedModule = action.payload.module;
        state.selectedExecution = action.payload.execution;
      })
      .addCase(getModuleExecutionAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getModuleExecutionAsync.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getModuleExecutionAsync.fulfilled, (state) => {
        state.loading = false;
      });
  },
});

export const moduleSelectionReducer = slice.reducer;
export const moduleSelectionActions = slice.actions;
export const moduleSelectionThunks = {
  fetchModuleExecutionsForYearAsync,
  fetchModuleFilter,
  getExecutionsAsync,
  getModuleExecutionAsync,
  setFilterAsync,
};
